import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';
import Logo from './Logo';

const NAV_LINKS = [
  ['/', 'header.home'],
  ['/about', 'header.about'],
  ['/products', 'header.products'],
  ['/pricing', 'header.pricing'],
  ['/blog', 'header.blog'],
  ['/contact', 'header.contact'],
  ['/ip-policy', 'header.ip']
];

const LANGUAGES = [
  ['es', 'Español'],
  ['en', 'English']
];

const Header = () => {
  const { t, i18n } = useTranslation();
  const { headerColor } = useTheme();
  const { pathname } = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const currentLanguage = (i18n.resolvedLanguage || i18n.language || 'es').slice(0, 2);

  const closeMenu = () => setIsMenuOpen(false);

  const linkClass = (path) =>
    `transition-colors hover:text-secondary ${pathname === path ? 'font-semibold text-white underline underline-offset-8 decoration-secondary decoration-2' : 'text-white/90'}`;

  const languageButtons = (className) =>
    LANGUAGES.map(([code, label]) => (
      <button
        key={code}
        type="button"
        lang={code}
        aria-pressed={currentLanguage === code}
        onClick={() => {
          i18n.changeLanguage(code);
          closeMenu();
        }}
        className={`${className} ${currentLanguage === code ? 'bg-white text-primary font-semibold' : 'text-white/90 hover:text-secondary'}`}
      >
        {label}
      </button>
    ));

  return (
    <header className="shadow-md sticky top-0 z-50 border-b-2" style={{ backgroundColor: headerColor, borderBottomColor: '#043a52' }}>
      <div className="container py-4 flex justify-between items-center">
        <Link to="/" onClick={closeMenu} aria-label="Rokwansoft">
          <Logo size="large" color="white" textColor="white" />
        </Link>

        <nav aria-label={t('header.home')} className="hidden md:flex space-x-8">
          {NAV_LINKS.map(([path, key]) => (
            <Link key={path} to={path} aria-current={pathname === path ? 'page' : undefined} className={linkClass(path)}>
              {t(key)}
            </Link>
          ))}
        </nav>

        <div role="group" aria-label={t('header.language')} className="hidden md:flex items-center gap-1 rounded-md border border-white/30 p-0.5 text-sm">
          {languageButtons('px-3 py-1 rounded transition-colors')}
        </div>

        <button
          type="button"
          aria-label={isMenuOpen ? t('header.closeMenu') : t('header.openMenu')}
          aria-expanded={isMenuOpen}
          aria-controls="mobile-menu"
          className="md:hidden text-white hover:text-secondary p-2 -mr-2"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isMenuOpen ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16M4 18h16'} />
          </svg>
        </button>
      </div>

      {isMenuOpen && (
        <div id="mobile-menu" className="md:hidden border-t border-white/20" style={{ backgroundColor: headerColor }}>
          <nav aria-label={t('header.home')} className="container py-2 flex flex-col">
            {NAV_LINKS.map(([path, key]) => (
              <Link
                key={path}
                to={path}
                onClick={closeMenu}
                aria-current={pathname === path ? 'page' : undefined}
                className={`py-3 ${linkClass(path)}`}
              >
                {t(key)}
              </Link>
            ))}
          </nav>
          <div role="group" aria-label={t('header.language')} className="container pb-4 pt-2 flex gap-2 text-sm">
            {languageButtons('px-4 py-2 rounded-md border border-white/30 transition-colors')}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
